import { MapStyle } from '../../../../common/types';
import CanvasObject from '../CanvasObject';
import { opacityWhileDragging } from '../common';
import type SurfaceBase from '../SurfaceBase';
import type { LngLat, Point } from '../SurfaceBase';
import { markerBboxPadding, markerLineWidth, markerRadius } from './common';

export default class EditMarkerObject extends CanvasObject {
  dragOffset: Point = { x: NaN, y: NaN };
  private point: Point;
  private isDragging = false;
  private onMarkerUpdated: (p: LngLat) => void;

  constructor(id: string, surface: SurfaceBase, onMarkerUpdated: (p: LngLat) => void) {
    super(id, surface);
    const marker = this.getCurrentMarkerObject();
    if (!marker) throw new Error('currentObject is not a marker!');

    this.point = this.surface.toScreenCoords({
      lng: marker.geometry.coordinates[0],
      lat: marker.geometry.coordinates[1],
    });

    this.onMarkerUpdated = onMarkerUpdated;
    this.interactive = true;

    this.on('dragStart', () => {
      this.isDragging = true;
      this.renderAll();
    });
    this.on('dragging', (e) => {
      this.point = e.point;
      this.renderAll();
    });
    this.on('dragEnd', () => {
      this.isDragging = false;
      this.onMarkerUpdated(this.surface.toMapCoords(this.point));
      this.renderAll();
    });
  }

  getCurrentMarkerObject() {
    const currentObject = this.getStore().currentObject;
    if (currentObject?.type !== 'marker') return;
    return currentObject;
  }

  refreshScreenCoords(): void {
    const marker = this.getCurrentMarkerObject();
    if (!marker) return;
    this.point = this.surface.toScreenCoords({
      lng: marker.geometry.coordinates[0],
      lat: marker.geometry.coordinates[1],
    });
  }

  render(ctx: CanvasRenderingContext2D) {
    const marker = this.getCurrentMarkerObject();
    if (!marker) return;
    const store = this.getStore();
    const { title, properties } = marker;
    ctx.beginPath();
    ctx.strokeStyle = 'white';
    ctx.fillStyle = properties.color;
    ctx.lineWidth = markerLineWidth;
    ctx.arc(this.point.x, this.point.y, markerRadius, 0, 2 * Math.PI);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    this.drawBoundingBox(ctx, properties.color, false);

    ctx.globalAlpha = 1;

    // not really needed for marker objects but it still improves UX a bit.
    if (this.isDragging) {
      ctx.globalAlpha = opacityWhileDragging;
    }

    // save some compute by not rendering text when dragging
    if (!this.isDragging) {
      ctx.lineWidth = 0.5;
      if (
        !store.currentMap.isLoading &&
        (store.currentMap.baseMap === MapStyle.satellite || store.currentMap.baseMap === MapStyle.dark)
      ) {
        ctx.strokeStyle = 'black';
        ctx.fillStyle = 'white';
      } else {
        ctx.strokeStyle = 'white';
        ctx.fillStyle = 'black';
      }

      ctx.font = 'bold 1.2rem sans-serif';
      ctx.textBaseline = 'middle';
      ctx.fillText(title, this.point.x + markerBboxPadding, this.point.y);
      ctx.strokeText(title, this.point.x + markerBboxPadding, this.point.y);
    }
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D, color: string): void {
    this.drawBoundingBox(ctx, color, true);
  }

  drawBoundingBox(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    color: string,
    isHitArea: boolean = false,
  ) {
    ctx.beginPath();
    const x = this.point.x - markerBboxPadding;
    const y = this.point.y - markerBboxPadding;
    ctx.strokeStyle = color;
    ctx.lineWidth = markerLineWidth;
    ctx.rect(x, y, markerBboxPadding * 2, markerBboxPadding * 2);

    ctx.stroke();

    if (isHitArea) {
      ctx.fillStyle = color;
      ctx.fillRect(x, y, markerBboxPadding * 2, markerBboxPadding * 2);
    }
  }
}
